import React, { useEffect, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"

dayjs.extend(utc);
dayjs.extend(timezone);

import countryList from "react-select-country-list";
import "react-phone-input-2/lib/bootstrap.css";
import { ReactComponent as DownArrow } from "../../../../../../assets/AccountingModule/arrow_down.svg";
import color from "../../../../../../constants/color";
import { paymentTermsOptions } from "../../../../../../constants/data";
import CustomInput from "../../../.././../../components/CustomInput";
import DropdownIcon from "../../../../../../assets/AccountingModule/drop_down_icon.svg";
import EditIcon from "../../../../../../assets/AccountingModule/edit.svg";
import style from "./style";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../../../api/AuthService";
import { useAlert } from "../../../../../../context/AlertContext";

const CreateNewInvoicePage = () => {
    const { showAlert } = useAlert();
    const navigate = useNavigate()
    const location = useLocation();
    const salesOrderData = location.state?.item || {};
    const itemList = location.state?.itemList || [];
    const [customer, setCustomer] = useState(null);
    const [product, setProduct] = useState(
        salesOrderData?.itemInformation
            ? salesOrderData.itemInformation.map(item => {
                return ({
                    name: item.name,
                    productId: item.itemId,
                    desc: item.description,
                    unit: item.rate,
                    quantity: item.quantity,
                    amount: "",
                    invoiced: item.invoiced,
                    invoiceQuantity: ""
                });
            })
            : [{ name: "", productId: "", desc: "", unit: "", quantity: "", invoiced: "", amount: "", invoiceQuantity: "" }]
    );
    const [totalAmount, setTotalAmount] = useState(salesOrderData?.total || 0);
    const [invoiceDate, setInvoiceDate] = useState(dayjs());
    const [paymentTerms, setPaymentTerms] = useState(salesOrderData?.paymentTerm || "");
    const [dueDate, setDueDate] = useState(dayjs().add(5, 'day'));
    const [penaltyAmount, setPenaltyAmount] = useState(0);
    const [otherInfo, setOtherInfo] = useState(salesOrderData?.customerNotes || "N/A");
    const [discountPercentage, setDiscountPercentage] = useState(salesOrderData?.discountPercentage || 0);
    const [taxPercentage, setTaxPercentage] = useState(salesOrderData?.taxPercentage || 0);

    // Billing Address
    const [billingCountry, setBillingCountry] = useState(salesOrderData?.customer?.billingAddress?.country || "");
    const [billingStreet1, setBillingStreet1] = useState(salesOrderData?.customer?.billingAddress?.street1 || "");
    const [billingStreet2, setBillingStreet2] = useState(salesOrderData?.customer?.billingAddress?.street2 || "");
    const [billingCity, setBillingCity] = useState(salesOrderData?.customer?.billingAddress?.city || "");
    const [billingState, setBillingState] = useState(salesOrderData?.customer?.billingAddress?.state || "");
    const [billingPinCode, setBillingPinCode] = useState(salesOrderData?.customer?.billingAddress?.pinCode || "");
    const [billingPhone, setBillingPhone] = useState(salesOrderData?.customer?.billingAddress?.phone || "");

    // Shipping Address
    const [shippingCountry, setShippingCountry] = useState(salesOrderData?.customer?.shippingAddress?.country || "");
    const [shippingStreet1, setShippingStreet1] = useState(salesOrderData?.customer?.shippingAddress?.street1 || "");
    const [shippingStreet2, setShippingStreet2] = useState(salesOrderData?.customer?.shippingAddress?.street2 || "");
    const [shippingCity, setShippingCity] = useState(salesOrderData?.customer?.shippingAddress?.city || "");
    const [shippingState, setShippingState] = useState(salesOrderData?.customer?.shippingAddress?.state || "");
    const [shippingPinCode, setShippingPinCode] = useState(salesOrderData?.customer?.shippingAddress?.pinCode || "");
    const [shippingPhone, setShippingPhone] = useState(salesOrderData?.customer?.shippingAddress?.phone || "");

    useEffect(() => {
        if (salesOrderData) {
            // Set dates from sales order data
            setInvoiceDate(dayjs(salesOrderData.salesOrderDate));
            setDueDate(dayjs(salesOrderData.expectedShipmentDate));

            // Set calculated values
            setTotalAmount(salesOrderData.total);
            setDiscountPercentage(salesOrderData.discountPercentage);
            setTaxPercentage(salesOrderData.taxPercentage);

            // Set customer notes
            setOtherInfo(salesOrderData.customerNotes || "N/A");
            setCustomer(salesOrderData.customer);

            // Set dates from sales order data
            setInvoiceDate(dayjs(salesOrderData.salesOrderDate));
            setDueDate(dayjs(salesOrderData.expectedShipmentDate));

            // Set calculated values
            setTotalAmount(salesOrderData.total);
            setDiscountPercentage(salesOrderData.discountPercentage);
            setTaxPercentage(salesOrderData.taxPercentage);

            // Set customer notes
            setOtherInfo(salesOrderData.customerNotes || "N/A");
            setCustomer(salesOrderData.customer);

            // Set dates from sales order data
            setInvoiceDate(dayjs(salesOrderData.salesOrderDate));
            setDueDate(dayjs(salesOrderData.expectedShipmentDate));

            // Set calculated values
            setTotalAmount(salesOrderData.total);
            setDiscountPercentage(salesOrderData.discountPercentage);
            setTaxPercentage(salesOrderData.taxPercentage);

            // Set customer notes
            setOtherInfo(salesOrderData.customerNotes || "N/A");
        }
    }, [salesOrderData]);

    const handleCopyBillingToShipping = () => {
        setShippingCountry(billingCountry);
        setShippingStreet1(billingStreet1);
        setShippingStreet2(billingStreet2);
        setShippingCity(billingCity);
        setShippingState(billingState);
        setShippingPinCode(billingPinCode);
        setShippingPhone(billingPhone);
    };

    const handleAddField = () => {
        setProduct([
            ...product,
            { name: "", productId: "", desc: "", unit: "", invoiced: "", quantity: "", amount: "" },
        ]);
    };

    const handleProduct = (value, index, key) => {
        const updatedProductDetails = [...product];

        if (key === "name") {
            const selectedItem = itemList.find(item => item.title === value);
            if (selectedItem) {
                updatedProductDetails[index].productId = selectedItem._id;
                updatedProductDetails[index].desc = selectedItem.desc || "";
                updatedProductDetails[index].unit = Number(selectedItem.sellingPrice || 0);
                updatedProductDetails[index].quantity = 1;
                updatedProductDetails[index].invoiceQuantity = 0;
                updatedProductDetails[index].amount = Number(selectedItem.sellingPrice || 0);
            }
        } else {
            updatedProductDetails[index][key] = value;
            if (key === "invoiceQuantity" || key === "unit") {
                const qty = Number(updatedProductDetails[index].invoiceQuantity) || 0;
                const unit = Number(updatedProductDetails[index].unit) || 0;
                updatedProductDetails[index].amount = qty * unit;
            }
        }
        setProduct(updatedProductDetails);
        calculateTotalAmount();
    };

    const handleDeleteField = (index) => {
        const updatedProduct = [...product];
        updatedProduct.splice(index, 1);
        setProduct(updatedProduct);
        calculateTotalAmount();
    };

    const calculateTotalAmount = () => {
        let total = 0;
        product.forEach(item => {
            total += Number(item.amount) || 0;
        });
        setTotalAmount(total);
    };

    const calculateFinalAmounts = () => {
        const discountValue = totalAmount * (discountPercentage / 100);
        const subtotalAfterDiscount = totalAmount - discountValue;
        const taxValue = subtotalAfterDiscount * (taxPercentage / 100);
        const grandTotal = subtotalAfterDiscount + taxValue;

        return {
            discountValue,
            taxValue,
            grandTotal
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { discountValue, taxValue, grandTotal } = calculateFinalAmounts();

        const productInformation = product.map(item => ({
            productId: item.productId,
            name: item.name,
            description: item.desc,
            unitCost: item.unit,
            quantity: item.invoiceQuantity,
            amount: item.amount
        }));

        const payload = {
            customer: customer?._id,
            billingAddress: {
                country: billingCountry,
                street1: billingStreet1,
                street2: billingStreet2,
                city: billingCity,
                state: billingState,
                pinCode: billingPinCode,
                phone: billingPhone,
            },
            shippingAddress: {
                country: shippingCountry,
                street1: shippingStreet1,
                street2: shippingStreet2,
                city: shippingCity,
                state: shippingState,
                pinCode: shippingPinCode,
                phone: shippingPhone,
            },
            invoiceDate: dayjs(invoiceDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            paymentTerms: paymentTerms,
            dueDate: dayjs(dueDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            dailyPenaltyAmount: penaltyAmount,
            otherInformation: otherInfo,
            productInformation: productInformation,
            discountPercentage: discountPercentage,
            discountValue: discountValue,
            taxPercentage: taxPercentage,
            taxValue: taxValue,
            total: totalAmount,
            grandTotal: grandTotal
        };
        try {
            const result = await AuthService.handleAddNewInvoice(salesOrderData._id, payload);
            if (result?.success) {
                showAlert("Invoice created successfully!", "success");
                navigate("/dashboard/salesHome");
            } else {
                showAlert(result?.message || "Failed to create invoice", "error");
            }
        } catch (error) {
            showAlert("An error occurred while creating invoice", "error");
        }
    };


    const CustomDropdownIcon = () => (
        <Box sx={{ display: "flex", alignItems: "center", pr: 2 }}>
            <img src={DropdownIcon} alt="Dropdown" width={11} height={11} />
        </Box>
    );

    const { discountValue, taxValue, grandTotal } = calculateFinalAmounts();
    const options = countryList().getData();

    return (
        <>
            <Box px="15px" mt={2}>
                <Box sx={style.headerContainer}>
                    <Typography sx={style.leaveHeading}>New Invoice</Typography>
                </Box>
            </Box>
            <Box sx={{ ...style.itemSection, marginTop: 1 }}>
                <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
                <Typography sx={style.gridText}>Invoice Details</Typography>
            </Box>
            <form onSubmit={(e) => handleSubmit(e)}>
                <Grid container sx={style.mainContainer}>
                    {/* --- Invoice Details */}
                    <Grid item xs={12} lg={11.8} sx={style.gridbox}>
                        {/* Customer */}
                        <Box sx={style.inputSection}>
                            <Box sx={{ ...style.inputBox, width: "100%" }}>
                                <InputLabel sx={style.inputText}>
                                    Customer <span style={{ color: "red" }}>*</span>
                                </InputLabel>
                                <CustomInput
                                    id="customer-input"
                                    error={''}
                                    variant="outlined"
                                    defaultValue={salesOrderData?.customer?.name || 'N/A'}
                                    readOnly={true}
                                    placeholder="Select Customer"
                                    sx={{ ...style.modalInput, ...style.inputSpace, cursor: 'not-allowed' }}
                                />
                            </Box>
                        </Box>

                        {/* Billing and Shipping Address */}
                        <Grid container>
                            <Grid
                                item
                                xs={12}
                                lg={11.7}
                                sx={{
                                    ...style.gridbox,
                                    my: 0,
                                    mr: { xs: 0, sm: 0, md: 0, lg: 0 },
                                }}
                            >
                                <Box sx={style.addressBox}>
                                    <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography sx={{ fontWeight: "bold" }}>
                                                Billing Address
                                            </Typography>
                                            <img src={EditIcon} height={16} width={16} alt="edit" />
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>
                                                    Country
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <FormControl fullWidth required sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        height: "2.8em",
                                                        marginTop: { xs: "0em", md: "0.5em" },
                                                        borderRadius: "10px"
                                                    }
                                                }}>
                                                    <Select
                                                        // labelId="billing-country-label"
                                                        // id="billing-country"
                                                        value={billingCountry || ""}
                                                        onChange={(event) => setBillingCountry(event.target.value)}
                                                        name="billingCountry"
                                                        sx={style.dropDownMenu}
                                                        IconComponent={CustomDropdownIcon}
                                                        displayEmpty
                                                    >
                                                        <MenuItem disabled value="">Select</MenuItem>
                                                        {options.map((country, index) => (
                                                            <MenuItem key={index} sx={style.dropDownMenu} value={country.label}>{country.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{ 
                                                display: "flex",
                                                flexDirection: { xs: "column", md: "row" },
                                                justifyContent: "space-between" }}
                                        >
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Address</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Street 1"
                                                    value={billingStreet1}
                                                    onChange={(e) => setBillingStreet1(e.target.value)}
                                                    error={false}
                                                    autoComplete="bstreet1"
                                                    sx={{ ...style.addressTextField, mb: 3 }}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="Street 2"
                                                    value={billingStreet2}
                                                    onChange={(e) => setBillingStreet2(e.target.value)}
                                                    error={false}
                                                    autoComplete="bstreet2"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>City</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="City"
                                                    value={billingCity}
                                                    onChange={(e) => setBillingCity(e.target.value)}
                                                    error={false}
                                                    autoComplete="bcity"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>State</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="State"
                                                    value={billingState}
                                                    onChange={(e) => setBillingState(e.target.value)}
                                                    error={false}
                                                    autoComplete="bstate"
                                                    sx={style.addressTextField}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Pin Code</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Pin Code"
                                                    value={billingPinCode}
                                                    onChange={(e) => setBillingPinCode(e.target.value)}
                                                    error={false}
                                                    autoComplete="bpinCode"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{ ...style.addressInput, mb: 0 }}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Phone</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Phone"
                                                    value={billingPhone}
                                                    onChange={(e) => setBillingPhone(e.target.value)}
                                                    error={false}
                                                    autoComplete="bphone"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                                        <Box sx={{ display: "flex", gap: "8px" }}>
                                            <Box
                                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                            >
                                                <Typography sx={{ fontWeight: "bold" }}>
                                                    Shipping Address
                                                </Typography>
                                                <img src={EditIcon} height={16} width={16} alt="edit" />
                                            </Box>
                                            <Typography
                                                sx={{ color: color.secondary, cursor: "pointer" }}
                                                onClick={handleCopyBillingToShipping}
                                            >
                                                {"( "}
                                                <DownArrow /> Copy Billing Address{" )"}
                                            </Typography>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>
                                                    Country
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <FormControl fullWidth required sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        height: "2.8em",
                                                        marginTop: { xs: "0em", md: "0.5em" },
                                                        borderRadius: "10px"
                                                    }
                                                }}>
                                                    <Select
                                                        value={shippingCountry || ""}
                                                        onChange={(event) => setShippingCountry(event.target.value)}
                                                        name="shippingCountry"
                                                        sx={style.dropDownMenu}
                                                        displayEmpty
                                                        IconComponent={CustomDropdownIcon}
                                                    >
                                                        <MenuItem disabled value="">Select</MenuItem>
                                                        {options.map((country, index) => (
                                                            <MenuItem key={index} sx={style.dropDownMenu} value={country.label}>{country.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{ display: "flex", justifyContent: "space-between" }}
                                        >
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Address</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Street 1"
                                                    value={shippingStreet1}
                                                    onChange={(e) => setShippingStreet1(e.target.value)}
                                                    error={false}
                                                    autoComplete="sstreet1"
                                                    sx={{ ...style.addressTextField, mb: 3 }}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="Street 2"
                                                    value={shippingStreet2}
                                                    onChange={(e) => setShippingStreet2(e.target.value)}
                                                    error={false}
                                                    autoComplete="sstreet2"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>City</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="City"
                                                    value={shippingCity}
                                                    onChange={(e) => setShippingCity(e.target.value)}
                                                    error={false}
                                                    autoComplete="scity"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>State</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="State"
                                                    value={shippingState}
                                                    onChange={(e) => setShippingState(e.target.value)}
                                                    error={false}
                                                    autoComplete="sstate"
                                                    sx={style.addressTextField}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={style.addressInput}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Pin Code</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Pin Code"
                                                    value={shippingPinCode}
                                                    onChange={(e) => setShippingPinCode(e.target.value)}
                                                    error={false}
                                                    autoComplete="spinCode"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{ ...style.addressInput, mb: 0 }}>
                                            <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                                                <Typography sx={style.inputText}>Phone</Typography>
                                            </Box>
                                            <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                                                <TextField
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    placeholder="Phone"
                                                    value={shippingPhone}
                                                    onChange={(e) => setShippingPhone(e.target.value)}
                                                    error={false}
                                                    autoComplete="sphone"
                                                    sx={style.addressTextField}
                                                    InputProps={{
                                                        sx: {
                                                            fontSize: "14px",
                                                            fontWeight: 400,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={style.inputSection}>
                            <Box sx={style.inputBox}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <InputLabel sx={style.label}>Invoice Date</InputLabel>
                                    <DatePicker
                                        value={invoiceDate}
                                        onChange={(newValue) => {
                                            setInvoiceDate(newValue);
                                        }}
                                        sx={style.dateInput}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box sx={style.inputBox}>
                                <FormControl
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: "2.5em",
                                            marginTop: "0.4em",
                                            borderRadius: "10px",
                                        },
                                    }}
                                >
                                    <Typography sx={style.inputText}>
                                        Payment Terms<span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <Select
                                        onChange={(event) => setPaymentTerms(event.target.value)}
                                        name="paymentTerms"
                                        value={paymentTerms || ""}
                                        displayEmpty
                                    // IconComponent={CustomDropdownIcon}
                                    >
                                        <MenuItem disabled value="">
                                            Select
                                        </MenuItem>
                                        {paymentTermsOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ ...style.inputBox }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <InputLabel sx={style.label}>
                                        Due Date<span style={{ color: "red" }}>*</span>
                                    </InputLabel>
                                    <DatePicker
                                        value={dueDate}
                                        minDate={invoiceDate}
                                        onChange={(newValue) => setDueDate(newValue)}
                                        sx={{ ...style.dateInput }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Box>

                        <Box
                            sx={{ ...style.inputSection, justifyContent: "start", gap: 3.5 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={4}>
                                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                                        <Typography sx={style.inputText}>
                                            Daily Penalty Amount
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            required
                                            fullWidth
                                            type="number"
                                            id="penaltyAmount"
                                            placeholder="Daily Penalty Amount"
                                            error={""}
                                            name="penaltyAmount"
                                            value={penaltyAmount}
                                            onChange={(e, val) => setPenaltyAmount(val)}
                                            autoComplete="penaltyAmount"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={8}>
                                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                                        <Typography sx={style.inputText}>
                                            Other Information
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            required
                                            fullWidth
                                            error={""}
                                            placeholder="Other Information"
                                            value={otherInfo}
                                            onChange={(e, val) => setOtherInfo(val)}
                                            autoComplete="otherInfo"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* --- Product Information */}
                    <Box sx={style.itemSection}>
                        <Box
                            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
                        ></Box>
                        <Typography sx={style.gridText}>
                            Product Informations<span style={{ color: "red" }}>*</span>
                        </Typography>
                    </Box>

                    <Grid item xs={12} lg={11.8} sx={style.gridbox}>
                        {product.map((item, index) => {
                            const stockQuantity = item.quantity - item.invoiced
                            return (
                                <Box sx={style.leaveSection} key={index}>
                                    <Grid container sx={style.itemGrid}>
                                        <Grid item xs={12} lg={1.5}>
                                            <CustomInput
                                                placeholder="Product Name"
                                                defaultValue={item.name}
                                                value={item.name}
                                                variant="outlined"
                                                error={""}
                                                readOnly={true}
                                                sx={{ ...style.modalInput, ...style.inputSpace }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={2}>
                                            <CustomInput
                                                variant="outlined"
                                                fullWidth
                                                id="description"
                                                placeholder="Description"
                                                defaultValue={item.description}
                                                error={""}
                                                name="desc"
                                                value={item.description}
                                                onChange={(e, value) => handleProduct(value, index, "desc")}
                                                autoComplete="desc"
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Ordered"
                                                name="ordered"
                                                readOnly={true}
                                                error={""}
                                                defaultValue={item.quantity}
                                                value={item.quantity}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Invoiced"
                                                name="Invoiced"
                                                readOnly={true}
                                                defaultValue={item.invoiced}
                                                value={item.invoiced}
                                                error={""}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={1.5}>
                                            <CustomInput
                                                variant="outlined"
                                                placeholder="Unit Cost"
                                                type="number"
                                                error={""}
                                                defaultValue={item.unit}
                                                onChange={(e, val) => handleProduct(val, index, "unit")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={2}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                sx={{ ...style.customInput, width: "100%" }}
                                            >
                                                <TextField
                                                    variant="outlined"
                                                    value={item.invoiceQuantity}
                                                    type="number"
                                                    placeholder="Quantity"
                                                    sx={{
                                                        width: "100%",
                                                        paddingRight: "0px !important",
                                                        "& .MuiOutlinedInput-root": {
                                                            paddingRight: "0px !important",
                                                        },
                                                    }}
                                                    onChange={(e) => {
                                                        const value = parseFloat(e.target.value);
                                                        const maxQuantity = stockQuantity;

                                                        if (!isNaN(value) && value >= 0 && value <= maxQuantity) {
                                                            handleProduct(value, index, "invoiceQuantity");
                                                        } else if (e.target.value === "") {
                                                            handleProduct("", index, "invoiceQuantity");
                                                        } else {
                                                            const validValue = value < 0 ? 0 : maxQuantity;
                                                            handleProduct(validValue, index, "invoiceQuantity");
                                                        }
                                                    }}
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 0,
                                                            max: stockQuantity, // Set maximum value
                                                        },
                                                        endAdornment: (
                                                            <InputAdornment position="end" variant="filled">
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        bgcolor: "#DEDEDE",
                                                                        height: 43,
                                                                        p: 0,
                                                                        minWidth: 60, // Ensure minimum width
                                                                        maxWidth: 120, // Prevent excessive width
                                                                        ...style.inputBgBox,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            minWidth: 21,
                                                                            maxWidth: 80, // Limit width to prevent overflow
                                                                            height: "100%",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontWeight: "600",
                                                                            userSelect: "none",
                                                                            borderRadius: "8px",
                                                                            fontSize: "14px", // Smaller font for long numbers
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                            px: 0.5,
                                                                        }}
                                                                    >
                                                                        {stockQuantity}
                                                                    </Box>
                                                                    <Tooltip
                                                                        arrow
                                                                        placement="top"
                                                                        title={
                                                                            <Box
                                                                                sx={{
                                                                                    bgcolor: "white",
                                                                                    p: 1,
                                                                                    borderRadius: "6px",
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: "10px",
                                                                                        fontWeight: 600,
                                                                                        color: "#373D3F",
                                                                                        mb: 1,
                                                                                    }}
                                                                                >
                                                                                    Stock Details
                                                                                </Typography>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: "10px",
                                                                                        color: "#373D3F",
                                                                                        fontWeight: 400,
                                                                                    }}
                                                                                >
                                                                                    Stock on Hand :{" "}
                                                                                    {stockQuantity || "0"}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                        componentsProps={{
                                                                            tooltip: {
                                                                                sx: {
                                                                                    bgcolor: "white !important",
                                                                                    p: 0,
                                                                                    border:
                                                                                        "1px solid #878787 !important",
                                                                                    boxShadow: "none !important",
                                                                                    "& .MuiTooltip-arrow": {
                                                                                        color: "white !important",
                                                                                        "&::before": {
                                                                                            border: "1px solid #878787",
                                                                                            backgroundColor:
                                                                                                "white !important",
                                                                                        },
                                                                                    },
                                                                                },
                                                                            },
                                                                        }}
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            edge="end"
                                                                            sx={{
                                                                                color: "#878787",
                                                                                padding: "4px",
                                                                            }}
                                                                        >
                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={9} lg={1.5}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                placeholder="Amount"
                                                value={item.amount}
                                                InputProps={{
                                                    readOnly: true,
                                                    sx: {
                                                        borderRadius: "8px",
                                                        my: 1,
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        {index === 0 && (
                                            <Grid item xs={1.5} lg={0.5}>
                                                <Box sx={style.addIcon} onClick={handleAddField}>
                                                    +
                                                </Box>
                                            </Grid>
                                        )}
                                        {index !== 0 && (
                                            <Grid item xs={1.5} lg={0.5}>
                                                <Box
                                                    sx={style.deleteIcon}
                                                    onClick={() => handleDeleteField(index)}
                                                >
                                                    <DeleteOutlineIcon />
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Grid>

                    {/* --- Discount and Tax */}
                    <Grid container sx={style.lastGrid}>
                        <Box sx={style.itemSection}>
                            <Box
                                sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2, marginLeft: '-16px' }}
                            ></Box>
                            <Typography sx={style.gridText}>Discount and Tax(%)</Typography>
                        </Box>
                        <Grid item xs={12} lg={7.6} sx={style.gridboxs}>
                            <Grid container>
                                <Box sx={{ ...style.inputSection, width: "100%", gap: 3.5 }}>
                                    <Box sx={{ ...style.inputBox, width: "50%" }}>
                                        <Typography sx={style.inputText}>
                                            Discount<span style={{ color: "red" }}>*</span>
                                        </Typography>
                                        <CustomInput
                                            variant="outlined"
                                            fullWidth
                                            placeholder="(Discount)"
                                            error={""}
                                            type="number"
                                            defaultValue={discountPercentage}
                                            onChange={(e, val) => { setDiscountPercentage(Number(val)) }}
                                        />
                                    </Box>
                                    <Box sx={{ ...style.inputBox, width: "50%" }}>
                                        <Typography sx={style.inputText}>Tax</Typography>
                                        <CustomInput
                                            variant="outlined"
                                            fullWidth
                                            type="number"
                                            error={""}
                                            placeholder="Tax Percentage"
                                            name="tax"
                                            defaultValue={taxPercentage}
                                            onChange={(e, val) => { setTaxPercentage(Number(val)) }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            lg={4.2}
                            sx={{ ...style.gridboxs, marginTop: "-17px !important" }}
                        >
                            <Box sx={style.leaveSection}>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Total</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            {totalAmount.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Discount</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            -{discountValue.toFixed(2)} ({discountPercentage}%)
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>Tax</Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueText}>
                                            +{taxValue.toFixed(2)} ({taxPercentage}%)
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={style.inputContainerBox}>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueTexts}>
                                            Grand Total
                                        </Typography>
                                    </Box>
                                    <Box sx={style.key}>
                                        <Typography sx={style.descValueTexts}>
                                            {grandTotal.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Box sx={style.mainBoxes}>
                    <Box sx={style.createJob}>
                        <Button
                            variant="contained"
                            color="success"
                            // disabled={!isFormValid()}
                            type="submit"
                            sx={style.cancelButtons}
                        >
                            Create Invoice
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
};

export default CreateNewInvoicePage;
